import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Key, Trash2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { getElectronAPI } from '../../utils/electron';

/**
 * API Key management UI — only rendered in Electron.
 * Uses safeStorage via IPC for encrypted key storage.
 *
 * Visual: docked design from "Etch Designs.html" — left status icon,
 * provider name + ENV-var meta in middle, input/actions on the right.
 */
export default function ApiKeyManager({ providers, onKeySaved }) {
  const { t } = useTranslation();
  const api = getElectronAPI();
  const [keyStates, setKeyStates] = useState({});
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const providersWithKeys = providers.filter((p) => p.requiresApiKey);

  // Check which keys are already stored
  useEffect(() => {
    if (!api) return;
    const check = async () => {
      const states = {};
      for (const p of providersWithKeys) {
        const key = await api.getApiKey(p.id);
        states[p.id] = !!key;
      }
      setKeyStates(states);
    };
    check();
  }, [providers]);

  if (!api) return null;

  const handleSave = async (providerId) => {
    const value = inputs[providerId]?.trim();
    if (!value) return;

    setSaving(providerId);
    setError(null);
    try {
      const ok = await api.saveApiKey(providerId, value);
      if (ok) {
        setKeyStates((prev) => ({ ...prev, [providerId]: true }));
        setInputs((prev) => ({ ...prev, [providerId]: '' }));
        await onKeySaved?.(providerId);
      } else {
        setError(`Speichern fehlgeschlagen für ${providerId}. Verschlüsselung evtl. nicht verfügbar.`);
      }
    } catch (err) {
      setError(`Fehler beim Speichern für ${providerId}: ${err.message}`);
    }
    setSaving(null);
  };

  const handleDelete = async (providerId) => {
    setError(null);
    await api.deleteApiKey(providerId);
    setKeyStates((prev) => ({ ...prev, [providerId]: false }));
    await onKeySaved?.(providerId);
  };

  return (
    <div className="keys-list">
      {error && (
        <div className="field-msg field-msg-error" role="alert">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      {providersWithKeys.map((provider) => {
        const isSet = !!keyStates[provider.id];
        return (
          <div key={provider.id} className={`key-row ${isSet ? 'is-set' : ''}`}>
            <div className="key-row-icon" aria-hidden="true">
              {isSet ? <Check size={20} /> : <Key size={20} />}
            </div>
            <div className="key-row-body">
              <div className="key-row-head">
                <span className="key-row-name">{provider.name}</span>
                {isSet ? (
                  <span className="badge badge-ready">
                    <Check size={12} />
                    <span>{t('settings.configured', 'Konfiguriert')}</span>
                  </span>
                ) : (
                  <span className="badge badge-warn">
                    <AlertCircle size={12} />
                    <span>{t('settings.notConfigured', 'Nicht eingerichtet')}</span>
                  </span>
                )}
              </div>
              <div className="key-row-meta">
                <code>{provider.envKey}</code>
                {!isSet && (
                  <span style={{ marginLeft: 10 }}>
                    {t('settings.noKey', 'Kein Key hinterlegt — Anleitung in der Anbieter-Karte oben.')}
                  </span>
                )}
              </div>
            </div>
            <div className="key-row-actions">
              {!isSet ? (
                <>
                  <input
                    type="password"
                    className="input"
                    placeholder={`${provider.envKey}…`}
                    value={inputs[provider.id] || ''}
                    onChange={(e) => setInputs((prev) => ({ ...prev, [provider.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave(provider.id);
                    }}
                    aria-label={`${t('settings.newKeyFor', 'Neuer Key für')} ${provider.name}`}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!inputs[provider.id]?.trim() || saving === provider.id}
                    onClick={() => handleSave(provider.id)}
                  >
                    {saving === provider.id ? (
                      <>
                        <span className="btn-spinner" aria-hidden="true" />
                        <span>…</span>
                      </>
                    ) : (
                      t('settings.saveKey', 'Speichern')
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn-danger-ghost"
                  aria-label={`${t('settings.removeKey', 'Key entfernen')} – ${provider.name}`}
                  onClick={() => handleDelete(provider.id)}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
